import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { acceptOnFarmBillingAgreement, getOnFarmBillingAgreementStatus } from '../../api/onFarmBillingAgreementApi';
import { useAppSelector } from '../../redux/hooks';
import { OnFarmBillingAgreementStatus } from '../../types/interfaces/onFarmBillingAgreement.interfaces';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';

type OnFarmAgreementGateContextValue = {
  status: OnFarmBillingAgreementStatus | null;
  loading: boolean;
  requiresAgreementGate: boolean;
  showAgreementModal: boolean;
  agreementSubmitting: boolean;
  openAgreementModal: () => void;
  closeAgreementModal: () => void;
  refreshStatus: () => Promise<void>;
  acceptAgreement: () => Promise<boolean>;
};

const OnFarmAgreementGateContext = createContext<OnFarmAgreementGateContextValue | undefined>(undefined);

export const OnFarmAgreementGateProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const [status, setStatus] = useState<OnFarmBillingAgreementStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementSubmitting, setAgreementSubmitting] = useState(false);
  const [autoPromptedAccountId, setAutoPromptedAccountId] = useState<number | null>(null);

  const refreshStatus = useCallback(async () => {
    if (!accountId) {
      setStatus(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await getOnFarmBillingAgreementStatus();
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!status?.requiresAgreementGate || !accountId) {
      return;
    }

    if (autoPromptedAccountId === accountId) {
      return;
    }

    setShowAgreementModal(true);
    setAutoPromptedAccountId(accountId);
  }, [status?.requiresAgreementGate, accountId, autoPromptedAccountId]);

  const acceptAgreement = useCallback(async () => {
    if (!status?.currentTermsVersion) {
      return false;
    }

    setAgreementSubmitting(true);
    try {
      await acceptOnFarmBillingAgreement({
        termsVersion: status.currentTermsVersion,
      });
      await refreshStatus();
      setShowAgreementModal(false);
      return true;
    } catch (error: any) {
      showToast.error(error?.response?.data?.title || toastMessages.SOMETHING_WENT_WRONG);
      return false;
    } finally {
      setAgreementSubmitting(false);
    }
  }, [status?.currentTermsVersion, refreshStatus]);

  const value = useMemo<OnFarmAgreementGateContextValue>(
    () => ({
      status,
      loading,
      requiresAgreementGate: Boolean(status?.requiresAgreementGate),
      showAgreementModal,
      agreementSubmitting,
      openAgreementModal: () => setShowAgreementModal(true),
      closeAgreementModal: () => setShowAgreementModal(false),
      refreshStatus,
      acceptAgreement,
    }),
    [status, loading, showAgreementModal, agreementSubmitting, refreshStatus, acceptAgreement],
  );

  return <OnFarmAgreementGateContext.Provider value={value}>{children}</OnFarmAgreementGateContext.Provider>;
};

export const useOnFarmAgreementGate = (): OnFarmAgreementGateContextValue => {
  const context = useContext(OnFarmAgreementGateContext);
  if (!context) {
    return {
      status: null,
      loading: false,
      requiresAgreementGate: false,
      showAgreementModal: false,
      agreementSubmitting: false,
      openAgreementModal: () => undefined,
      closeAgreementModal: () => undefined,
      refreshStatus: async () => undefined,
      acceptAgreement: async () => false,
    };
  }

  return context;
};
